import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { SortDescriptor } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { LeadDto } from 'api/Clients/Types/LeadDto'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { FlexBox } from 'components/Box'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getLeadFields } from 'components/Filter/getLeadFields'
import { Spinner } from 'components/Spinner'
import { ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTickmillProductsAction } from 'store/clients/clients.actions'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'
import {
  fetchAdministrationUsersAction,
  fetchCountriesAction,
  fetchFilterAction,
  fetchFilterTypesAction,
  fetchTickmillCompaniesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { LeadsApiClient } from 'utils/api/leadsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { leadsColumns } from './columns'
import { LeadsImportModal } from './LeadsImportModal'
import { normalizeLeadsSearchQuery, normalizeLeadsSortQuery } from './normalizer'

const useMapStateToProps = () => ({
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  tickmillProducts: useAppSelector((state) => state.clients.tickmillProducts),
  languages: useAppSelector((state) => state.clients.language),
  users: useAppSelector((state) => state.identity.administrationUsers),
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridState: useAppSelector((state) => state.grid),
  hasFilters: useAppSelector(selectGridFilters).length > 0,
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  return {
    fetchTickmillProducts: async () => dispatch(fetchTickmillProductsAction(clientsApiClient)),
    fetchUsers: async () => dispatch(fetchAdministrationUsersAction(identityApiClient)),
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchCountries: async () => dispatch(fetchCountriesAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

const RecordLeadsPage = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const setToast = useContext(ToastContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const leadsApiClient = useApiClient(LeadsApiClient)
  const navigate = useNavigate()

  const permissions = auth?.permissions || []
  const [leads, setLeads] = useState<LeadDto[]>([])
  const [isImportLeadsModalOpen, setImportLeadsModalOpen] = useState<boolean>(false)

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { filters, tickmillCompanies, tickmillProducts, users } = useMapStateToProps()
  const { fetchFilters, fetchTickmillCompanies, fetchTickmillProducts, fetchUsers } =
    useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<LeadDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await leadsApiClient.getLeads({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (response?.items) {
            setLeads(response.items)
          }

          if (isEmpty(filters)) await fetchFilters()
          if (isEmpty(tickmillCompanies)) await fetchTickmillCompanies()
          if (isEmpty(tickmillProducts)) fetchTickmillProducts()
          if (isEmpty(users)) await fetchUsers()
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<LeadDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(normalizeLeadsSearchQuery(filter ?? gridState.filter)) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeLeadsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await leadsApiClient.getLeads({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setLeads((response.items || []).map((x) => Object.assign({ selected: false }, x)))
      }

      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleImportLeadsModalResolve = async () => {
    await filterFetch()
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await leadsApiClient.getLeads({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setLeads(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await leadsApiClient.getLeads({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setLeads(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await leadsApiClient.getLeads({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setLeads(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await leadsApiClient.getLeads({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setLeads(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const goToCreateLeadPage = () => {
    navigate(links.createLead.url)
  }

  const normalizedLeads = useMemo(
    () =>
      leads.map((lead) => ({ ...lead, products: lead?.products?.map((p) => p.name).join(';') })),
    [leads]
  )

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isImportLeadsModalOpen && (
        <LeadsImportModal
          visible={isImportLeadsModalOpen}
          setToast={setToast}
          onResolve={handleImportLeadsModalResolve}
          onClose={() => setImportLeadsModalOpen(false)}
        />
      )}
      <Breadcrumbs crumbs={[{ title: 'Leads', link: links.recordsLeads.url }]} />
      <div className='records-clients'>
        <FlexBox style={{ marginBottom: 24 }}>
          <ButtonGroup>
            <Button
              type='button'
              onClick={() => setImportLeadsModalOpen(true)}
              disabled={!hasPermission(permissions, PermissionsEnum.CanImportLeads)}
            >
              Import Leads
            </Button>
            <Button
              type='button'
              variant='outline'
              onClick={goToCreateLeadPage}
              disabled={!hasPermission(permissions, PermissionsEnum.CanImportLeads)}
            >
              Create Lead
            </Button>
          </ButtonGroup>
        </FlexBox>
        <SelectFiltersFacade
          gridState={gridState}
          gridStatusActions={gridStatusActions}
          gridStatusState={gridStatusState}
          setGridFilters={gridActions.setGridFilters}
          filterFetch={filterFetch}
          filterType={FilterType.LEADS}
          filterFields={getLeadFields()}
          containerClassName='records-clients__filter'
          getQuery={getQuery}
        />
        <TableStateContext.Provider value={[tableData, setTableData]}>
          <TableWithExport data={normalizedLeads} columns={leadsColumns}>
            <Grid
              {...gridState}
              resizable
              data={leads}
              sort={gridState.sort}
              total={gridState.total}
              take={gridState.take}
              skip={gridState.skip}
              onSortChange={handleSortChange}
              pageable={true}
              pager={() => (
                <Pager
                  onSkipCountChange={handleSkipCountChange}
                  onPageChange={handleGridPageChange}
                  onPageSizeChange={handlePageSizeChange}
                  page={gridState.pageIndex}
                  pageSize={gridState.pageSize}
                  take={gridState.take || 10}
                  skip={gridState.skip || 0}
                />
              )}
              style={{ zIndex: 0 }}
            >
              {leadsColumns
                .filter((c) => c.show)
                .map((column, idx) => (
                  <Column key={idx + 2} {...column} />
                ))}
            </Grid>
          </TableWithExport>
        </TableStateContext.Provider>
      </div>
    </React.Fragment>
  )
}

export default RecordLeadsPage
